/* eslint-disable max-lines */
/*eslint max-lines: ["error", 600]*/
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontMerriweather, fontRoboto } from "@product/scmp-sdk";

import { AuthorNamesContainer } from "scmp-app/components/article/article-author-block/author-names/styles";
import { ArticleDivider } from "scmp-app/components/article/article-divider";
import { ArticleHeaderAdSlot } from "scmp-app/components/article/article-header-ad-slot";
import {
  ArticleImageContainer,
  DescriptionContainer as ArticleImageDescriptionContainer,
  DescriptionContainer,
} from "scmp-app/components/article/article-image/styles";
import { ArticleContentRightSection } from "scmp-app/components/article/article-render/article-content/article-content-right-section";
import { AdSlot as RightSectionAdSlot } from "scmp-app/components/article/article-render/article-content/article-content-right-section/styles";
import { pageStyle } from "scmp-app/components/article/article-render/article-content/standard-styles";
import { Container as TimelineItemContainer } from "scmp-app/components/article/article-render/live-article/timeline/item/styles";
import {
  StyledAdSlot,
  StyledArticleLiveTag,
  StyledContentHeadline,
} from "scmp-app/components/article/article-render/styles";
import {
  SectionLink,
  SectionTagsSpacer,
} from "scmp-app/components/article/article-sections/styles";
import {
  StyledEntityLink as AuthorNameEntityLink,
  StyledAuthorName,
} from "scmp-app/components/author/author-name/styles";
import { StyledLi, StyledUl } from "scmp-app/components/schema-render/content/styles";
import { VideoTitleContainer } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-footer/styles";
import { Container as SCMPYouTubeContainer } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-player/styles";
import {
  PreviewContainer,
  PreviewContentContainer,
  PreviewImage,
} from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-preview/styles";
import { SCMPYoutubeVideoContainer } from "scmp-app/components/scmp-youtube-video/styles";
import type { WithStickyStackedHeightProps } from "scmp-app/lib/styles";
import {
  setFullContentWidthStyles,
  setFullViewportWidthStyles,
  ZIndexLayer,
} from "scmp-app/lib/styles";

import { LiveArticleContent } from "./content";
import { LiveArticleSummary } from "./summary";
import { LiveArticleTimeline } from "./timeline";

export const LiveArticleContainer = styled.div`
  display: grid;

  ${setFullContentWidthStyles()}

  grid:
    "header-container" min-content
    "leading-image" min-content
    "content-container" min-content
    / 100%;

  max-inline-size: 600px;
  margin-inline: auto;

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 800px;
    padding-block-end: 42px;
  }

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    grid:
      "header-ad header-ad header-ad header-ad" min-content
      ". header-container header-container header-container" min-content
      ". leading-image . right" min-content
      ". content-container . right" min-content
      / minmax(0, 92px) minmax(650px, 776px) 40px 300px;

    min-inline-size: 990px;
    max-inline-size: 1208px;
    margin-inline: auto;
  }

  > *:empty {
    margin: 0;
    padding: 0;
  }
`;

export const HeaderContainer = styled.div`
  grid-area: header-container;

  display: grid;
  grid:
    "topic-and-section" min-content
    "live-tag-and-action-bar" min-content
    "headline" min-content
    "sub-headline" min-content
    "sponsor" min-content
    / 100%;

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    grid:
      ". topic-and-section . ." min-content
      ". live-tag-and-action-bar . ." min-content
      ". headline . ." min-content
      ". sub-headline . ." min-content
      ". sponsor . ." min-content
      ". action-widgets . ." min-content
      / minmax(0, 60px) 904px 92px minmax(0, 60px);
  }
`;

export const TopicAndSection = styled.div`
  grid-area: topic-and-section;

  display: flex;
  flex-flow: row wrap;
  gap: 8px 12px;
  align-items: flex-start;

  margin-block-start: 20px;

  ${props => props.theme.breakpoints.up("tablet")} {
    flex-flow: row nowrap;
    gap: 16px;

    margin-block-start: 40px;
  }

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    margin-block-start: 60px;
  }

  ${SectionTagsSpacer} {
    margin-inline: 4px;
  }

  ${SectionLink} {
    font-size: 12px;
    line-height: normal;

    ${props => props.theme.breakpoints.up("tablet")} {
      font-size: 14px;
    }

    span {
      color: #000000;
      font-weight: 400;
      font-size: inherit;
      font-family: ${fontRoboto};
      font-style: italic;
      line-height: inherit;
    }
  }
`;

export const UpdateTimeWrapper = styled.div`
  display: flex;
  align-items: center;

  color: #4585ff;
  font-weight: 700;
  font-size: 12px;
  font-family: ${fontRoboto};
  font-style: normal;
  line-height: normal;
  font-variant-numeric: lining-nums proportional-nums;
  ${props => props.theme.breakpoints.up("homeDesktop")} {
    font-size: 14px;
  }
`;

export const LiveTagAndActionBar = styled.div`
  grid-area: live-tag-and-action-bar;

  display: flex;
  gap: 12px;
  align-items: center;

  margin-block-start: 16px;
  ${StyledArticleLiveTag} {
    gap: 4px;

    block-size: 20px;
    padding-block: 4px;
    padding-inline: 4px 6px;
  }
  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 20px;
  }
`;

export const Headline = styled.div`
  grid-area: headline;

  margin-block-start: 12px;

  font-weight: 400;
  font-size: 20px;
  font-family: ${fontMerriweather};
  line-height: 28px;

  ${props => props.theme.breakpoints.up("tablet")} {
    font-size: 28px;
    line-height: 39.2px;
  }

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    font-size: 32px;
    line-height: 44.8px;
  }

  ${StyledContentHeadline} {
    display: inline;
  }

  ${StyledArticleLiveTag} {
    margin-inline-end: 13px;

    transform: translateY(-2px);

    ${props => props.theme.breakpoints.up("tablet")} {
      transform: translateY(-6px);
    }
  }
`;

export const SubHeadline = styled.div`
  grid-area: sub-headline;

  margin-block-start: 16px;

  color: #333333;
  font-weight: 400;
  font-size: 13px;
  font-family: ${fontMerriweather};
  line-height: 18.2px;
  font-variant-numeric: lining-nums proportional-nums;

  ${StyledUl} {
    padding-inline-start: 18px;
  }

  ${StyledLi} + ${StyledLi} {
    margin-block-start: 10px;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 20px;

    font-weight: 400;
    font-size: 18px;
    line-height: 25.2px;

    ${StyledUl} {
      padding-inline-start: 18px;
    }

    ${StyledLi} + ${StyledLi} {
      margin-block-start: 12px;
    }
  }
`;

export const ActionWidgets = styled.div`
  grid-area: action-widgets;

  display: flex;
  display: none;
  flex-flow: row nowrap;
  gap: 16px;
  align-items: center;

  margin-block-start: 16px;

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    display: flex;

    margin-block-start: 20px;
  }
`;

export const Sponsor = styled.div`
  grid-area: sponsor;
`;

export const Author = styled.div`
  grid-area: author;

  margin-block-start: 20px;

  border-block-start: 1px solid #000000;

  padding-block-start: 16px;

  ${AuthorNamesContainer} {
    ${StyledAuthorName}, ${AuthorNameEntityLink} {
      text-decoration-line: underline;
    }
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 32px;
  }
`;

export const Leading = styled.div`
  grid-area: leading-image;

  margin-block-start: 20px;

  ${ArticleImageDescriptionContainer} {
    display: none !important;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 32px;
  }
`;

export const ContentContainer = styled.div`
  grid-area: content-container;

  display: grid;
  grid:
    "author" min-content
    "publish-datetime" min-content
    "mobile-content-header-ad" min-content
    "update-alert" min-content
    "introduction" min-content
    "content" min-content
    "mobile-content-article-ad" min-content
    "paywall" min-content
    "live-subscribe" min-content
    "live-summary" min-content
    "live-content" min-content
    "bottom-carousel" min-content
    / 100%;

  inline-size: 100%;
  margin-inline: auto;
  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 656px;
  }
  ${props => props.theme.breakpoints.up("homeDesktop")} {
    grid:
      ". . author" min-content
      ". . publish-datetime" min-content
      ". . update-alert" min-content
      "timeline . introduction" min-content
      "timeline . content" min-content
      "timeline . paywall" min-content
      "timeline . live-subscribe" min-content
      "timeline . live-summary" min-content
      "timeline . live-content" min-content
      / 180px 40px minmax(0, 1fr);

    max-inline-size: 100%;
  }
`;

type LiveUpdateAlertContainerProps = {
  $isShow: boolean;
} & WithStickyStackedHeightProps;

export const LiveUpdateAlertContainer = styled.div<LiveUpdateAlertContainerProps>`
  grid-area: update-alert;

  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 40}px;
  z-index: ${ZIndexLayer.LiveUpdateAlert};

  display: flex;
  justify-content: center;
  align-items: center;

  inline-size: 100%;
  block-size: 0;

  opacity: 0;

  pointer-events: none;

  transition: opacity 0.1s ease-in-out;

  ${props =>
    props.$isShow
      ? css`
          opacity: 1;
          pointer-events: unset;
        `
      : null}

  ${props => props.theme.breakpoints.up("desktop")} {
    inset-block-start: 100px;
  }
`;

export const LiveUpdateAlert = styled.div`
  padding-block: 12px;
  padding-inline: 20px;

  color: #ffffff;
  font-size: 16px;
  font-family: ${fontRoboto};
  line-height: 19px;

  border-radius: 22.5px;

  background-color: #000000;

  cursor: pointer;

  opacity: 0.95;
`;

export const PublishDateTime = styled.div`
  grid-area: publish-datetime;

  margin-block-start: 8px;

  line-height: normal;

  ${props => props.theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: 656px;
  }
`;

export const Introduction = styled.div`
  grid-area: introduction;

  margin-block-start: 40px;

  color: #000000;
  font-weight: 700;
  font-size: 16px;
  font-family: ${fontRoboto};
  line-height: 16px;
  text-transform: uppercase;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 32px;
  }
`;

export const Content = styled.div`
  grid-area: content;

  margin-block-start: 20px;

  ${props => pageStyle(props)}

  ${SCMPYoutubeVideoContainer} {
    ${SCMPYouTubeContainer} {
      inline-size: calc(100% - 40px);
      max-inline-size: 600px;
      margin-inline: auto;

      ${props => props.theme.breakpoints.up("tablet")} {
        inline-size: 100%;
        max-inline-size: none;
      }
    }
    ${PreviewContainer} {
      inline-size: calc(100% - 40px);
      block-size: fit-content;
      min-inline-size: 240px;
      max-inline-size: 600px;
      margin-inline: auto;

      ${props => props.theme.breakpoints.up("tablet")} {
        inline-size: 100%;
        max-inline-size: none;
      }

      ${PreviewImage} {
        block-size: auto;

        ${props => props.theme.breakpoints.up("tablet")} {
          block-size: 100%;
        }
      }

      ${PreviewContentContainer} {
        padding: 16px;
      }
    }
    ${VideoTitleContainer} {
      inline-size: calc(100% - 40px);
      min-inline-size: 240px;
      max-inline-size: 600px;
      margin-block: 0;
      margin-inline: auto;
      padding-block: 12px 11px;

      ${props => props.theme.breakpoints.up("tablet")} {
        align-items: center;

        inline-size: 100%;
        max-inline-size: none;
      }
    }
  }
`;

export const LiveSubscribe = styled.div`
  grid-area: live-subscribe;

  margin-block: 40px;
  ${props => props.theme.breakpoints.up("homeDesktop")} {
    margin-block: 12px 32px;
  }
`;

export const StyledLiveArticleContent = styled(LiveArticleContent)`
  grid-area: live-content;
  ${ArticleImageContainer} {
    position: static;
    inset-inline: auto;

    inline-size: auto;
    margin-inline: 0;

    ${DescriptionContainer} {
      padding-inline: 0;

      color: #6f6f6f;
      font-size: 12px;
      line-height: 15.6px;

      ${props => props.theme.breakpoints.up("tablet")} {
        font-size: 14px;
        line-height: 18.2px;
      }
    }
  }
`;

export const StyledLiveArticleSummary = styled(LiveArticleSummary)`
  grid-area: live-summary;
`;

export const TimelineContainer = styled.div<WithStickyStackedHeightProps>`
  grid-area: timeline;

  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 43}px;

  display: none;
  align-self: flex-start;

  margin-block-start: 32px;

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    display: flex;
    flex: 1;
  }
`;

export const StyledLiveArticleTimeline = styled(LiveArticleTimeline)`
  block-size: 95vh;

  overflow: scroll;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &:after {
    content: "";

    display: block;

    block-size: 40%;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  ${TimelineItemContainer} {
    margin-inline-start: 3px;
  }
`;

export const StyledArticleContentRightSection = styled(ArticleContentRightSection)`
  grid-area: right;

  margin-block-start: 32px;

  ${props => props.theme.breakpoints.between("mobile", "homeDesktop")} {
    display: none;
  }

  ${RightSectionAdSlot} {
    block-size: 100%;
  }
`;

export const RightAvailableSpaceTracker = styled.div`
  grid-row: content-start / topic-end;
  grid-column: span 1;

  display: none;

  inline-size: 0;

  ${props => props.theme.breakpoints.up("desktop")} {
    display: flex;
  }
`;

export const NewsletterWidgetContainer = styled.div<WithStickyStackedHeightProps>`
  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 50}px;

  padding-block: 16px;
  padding-inline: 0;

  border: 1px solid #d8d8d8;
  border-width: 1px 0;
`;

export const DesktopHeaderAdSlot = styled(ArticleHeaderAdSlot)`
  grid-area: header-ad;

  display: none;

  ${props => props.theme.breakpoints.up("desktop")} {
    display: block;
  }

  ${setFullContentWidthStyles()}
`;

export const MobileContentHeaderAdSlot = styled.div`
  grid-area: mobile-content-header-ad;

  display: none;

  margin-block-start: 32px;

  ${props => props.theme.breakpoints.down("homeDesktop")} {
    display: block;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 44px;
  }

  ${StyledAdSlot} {
    inline-size: fit-content;
    margin: auto;
  }
`;

export const MobileContentArticleAdSlot = styled.div`
  grid-area: mobile-content-article-ad;

  display: none;

  margin-block-start: 20px;
  padding-block: 12px;
  padding-inline: 0;

  ${props => props.theme.breakpoints.down("homeDesktop")} {
    display: block;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 0;
  }

  ${StyledAdSlot} {
    inline-size: fit-content;
    margin: auto;
  }
`;

export const Paywall = styled.div`
  grid-area: paywall;
`;

export const BottomCarousel = styled.div`
  grid-area: bottom-carousel;

  margin-block-start: 32px;

  :empty {
    display: none;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    inline-size: 100%;
    max-inline-size: 656px;
    margin-inline: auto;
  }

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    display: none;
  }
`;

export const StyledArticleDivider = styled(ArticleDivider)`
  ${setFullViewportWidthStyles()}
  margin-block-end: 40px;
  &:last-child {
    display: flex;
  }
  ${props => props.theme.breakpoints.only("mobile")} {
    ${setFullViewportWidthStyles()}
  }
`;
